import cn from "classnames";
import camelCase from "lodash/camelCase";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { getDataTestId } from "../../../utils/getDataTestId";
import { WSBadge, WSBadgeProps } from "../common/WSBadge/WSBadge.component";
import { WSPill, WSPillProps } from "../common/WSPill/WSPill.component";
import { WSIcon } from "../core/WSIcon/WSIcon.component";
import { WSText } from "../core/WSText/WSText.component";
import { WSElement, WSElementProps } from "../WSElement/WSElement.component";

import styles from "./WSTabs.module.scss";

export interface WSTab {
  label: string;
  render?: () => React.ReactNode;
  badge?: number | WSBadgeProps;
  pill?: WSPillProps;
  onClick?: () => void;
}

export type TabChangeHandler = (newIndex: number) => void;

export interface WSTabsNavigationProps extends WSElementProps {
  tabs: Omit<WSTab, "render">[];
  activeIndex?: number;
  onTabChange?: TabChangeHandler;
  name?: string;
}

export const WSTabsNavigation: React.FC<WSTabsNavigationProps> = ({
  tabs,
  activeIndex,
  onTabChange,
  className,
  name,
  ...otherProps
}) => {
  const testIdBase = getDataTestId("tabs", name);
  const refContent = useRef<HTMLDivElement>(null);
  const [isButtonBackwardVisible, setIsButtonBackwardVisible] = useState(false);
  const [isButtonForwardVisible, setIsButtonForwardVisible] = useState(false);

  const updateButtonsState = useCallback(() => {
    if (!refContent.current) return;

    const { scrollLeft, scrollWidth, clientWidth: width } = refContent.current;

    setIsButtonBackwardVisible(scrollLeft > 0);
    setIsButtonForwardVisible(Math.ceil(scrollLeft) >= scrollWidth - width);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", updateButtonsState);
    updateButtonsState();
    return () => window.removeEventListener("resize", updateButtonsState);
  }, [updateButtonsState]);

  const onButtonBackwardClick = useCallback(() => {
    if (!refContent.current) return;

    const { scrollLeft, clientWidth: width } = refContent.current;
    const pos = scrollLeft - width;

    refContent.current.scrollLeft = pos <= 0 ? 0 : pos;
  }, []);

  const onButtonForwardClick = useCallback(() => {
    if (!refContent.current) return;

    const { scrollLeft, clientWidth: width, scrollWidth } = refContent.current;
    const pos = scrollLeft + width;

    refContent.current.scrollLeft = pos >= scrollWidth ? scrollWidth : pos;
  }, []);

  // TODO(artem): revisit icons once we update WSIcon
  const buttonBackward = useMemo(
    () => (
      <WSElement
        role="button"
        aria-label="Scroll tabs left"
        onClick={onButtonBackwardClick}
        className={styles.buttonPrevious}
      >
        <WSIcon block size="M" name="chevron-left" aria-hidden="true" />
      </WSElement>
    ),
    [onButtonBackwardClick]
  );
  const buttonForward = useMemo(
    () => (
      <WSElement
        role="button"
        aria-label="Scroll tabs right"
        onClick={onButtonForwardClick}
        className={styles.buttonNext}
      >
        <WSIcon block size="M" name="chevron-right" aria-hidden="true" />
      </WSElement>
    ),
    [onButtonForwardClick]
  );

  return (
    <WSElement
      data-testid={`${testIdBase}Navigation`}
      className={cn(styles.navigationContainer, className)}
      {...otherProps}
    >
      {isButtonBackwardVisible && buttonBackward}

      <WSElement
        ref={refContent}
        className={styles.navigationContent}
        onScroll={updateButtonsState}
      >
        <WSElement role="tablist" className={styles.navigation}>
          {tabs.map((tab, index) => {
            const isActive = activeIndex === index;
            const tabIdSuffix = camelCase(tab.label) || index;
            return (
              <WSElement
                key={tab.label}
                role="tab"
                aria-selected={isActive}
                data-testid={`${testIdBase}Tab-${tabIdSuffix}`}
                className={cn(styles.item, isActive && styles.active)}
                onClick={() => {
                  onTabChange?.(index);
                  tab.onClick?.();
                }}
              >
                <WSText.ParagraphSm className={styles.label}>
                  {tab.label}
                </WSText.ParagraphSm>

                {typeof tab.badge === "number" ? (
                  <WSBadge
                    theme={isActive ? "dark" : "neutral"}
                    size="S"
                    ml="S"
                    number={tab.badge}
                  />
                ) : tab.badge ? (
                  <WSBadge
                    theme={isActive ? "dark" : "neutral"}
                    size="S"
                    ml="S"
                    {...tab.badge}
                  />
                ) : null}
                {tab.pill ? <WSPill ml="S" {...tab.pill} /> : null}
              </WSElement>
            );
          })}
        </WSElement>
      </WSElement>

      {!isButtonForwardVisible && buttonForward}
    </WSElement>
  );
};

export interface WSTabsContentProps extends WSElementProps {}

export const WSTabsContent: React.FC<WSTabsContentProps> = ({
  className,
  ...otherProps
}) => (
  <WSElement
    role="tabpanel"
    className={cn(styles.content, className)}
    {...otherProps}
  />
);

export type WSTabsProps = {
  tabs: WSTab[];
  initialIndex?: number;
  onTabChange?: TabChangeHandler;
  navigationClassName?: string;
  contentClassName?: string;
  name?: string;
} & WSElementProps;

export const WSTabs: React.FC<WSTabsProps> = ({
  tabs,
  onTabChange,
  initialIndex,
  className,
  navigationClassName,
  contentClassName,
  name,
  ...otherProps
}) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex ?? 0);

  const activeTab = tabs[activeIndex];
  const testIdBase = getDataTestId("tabs", name);
  const activeTabIdSuffix = camelCase(activeTab?.label) || activeIndex;

  return (
    <WSElement
      data-testid={testIdBase}
      className={className}
      {...otherProps}
    >
      <WSTabsNavigation
        tabs={tabs}
        activeIndex={activeIndex}
        onTabChange={(index) => {
          setActiveIndex(index);
          onTabChange?.(index);
        }}
        className={navigationClassName}
        name={name}
      />
      {activeTab.render && (
        <WSTabsContent
          data-testid={`${testIdBase}Panel-${activeTabIdSuffix}`}
          className={contentClassName}
        >
          {activeTab.render()}
        </WSTabsContent>
      )}
    </WSElement>
  );
};
